import React, { useEffect, useState } from 'react';
import {
  Row, Col, Card, Statistic, Typography, Spin, Space, Tag,
  Table, Button, Tooltip, message, Alert, Badge,
} from 'antd';
import {
  ThunderboltOutlined, CheckCircleOutlined, ClockCircleOutlined,
  WarningOutlined, ReloadOutlined, DatabaseOutlined,
  NodeExpandOutlined, DesktopOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { theme as antdTheme } from 'antd';
import axios from 'axios';

const { Title, Text } = Typography;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AdminSystem = () => {
  const { token: tk } = antdTheme.useToken();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get('/api/admin/system', { headers: authHeader() });
      setData(res.data);
    } catch (err) {
      message.error('Failed to load system health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!data)   return <Alert type="error" message="Failed to load system data" showIcon />;

  const { queue, failed_jobs, db, uptime_seconds, memory_mb, node_version } = data;

  const uptimeFmt = () => {
    const h = Math.floor(uptime_seconds / 3600);
    const m = Math.floor((uptime_seconds % 3600) / 60);
    const s = uptime_seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const queueCards = [
    { label: 'Waiting',   value: queue.waiting,   color: '#0a40d9', icon: <ClockCircleOutlined /> },
    { label: 'Active',    value: queue.active,    color: '#f59e0b', icon: <ThunderboltOutlined /> },
    { label: 'Completed', value: queue.completed, color: '#10b981', icon: <CheckCircleOutlined /> },
    { label: 'Failed',    value: queue.failed,    color: queue.failed > 0 ? '#ef4444' : '#9ca3af', icon: <CloseCircleOutlined /> },
    { label: 'Delayed',   value: queue.delayed,   color: '#2e6bf5', icon: <ClockCircleOutlined /> },
  ];

  const failedCols = [
    { title: 'Job ID',      dataIndex: 'id',          key: 'id',     width: 100, render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: 'To',          dataIndex: 'to',          key: 'to',     render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text> },
    { title: 'Campaign',    dataIndex: 'campaign_id', key: 'camp',   width: 100, render: v => v || '—' },
    { title: 'Attempts',    dataIndex: 'attempts',    key: 'att',    width: 80, align: 'center' },
    { title: 'Failed At',   dataIndex: 'failed_at',   key: 'at',     width: 160,
      render: d => d ? new Date(d).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—' },
    { title: 'Reason',      dataIndex: 'reason',      key: 'reason',
      render: v => <Text type="secondary" style={{ fontSize: 11 }}>{v?.slice(0, 80)}{v?.length > 80 ? '…' : ''}</Text> },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>System Health</Title>
          <Text type="secondary">Queue status, server metrics and failed jobs.</Text>
        </div>
        <Button
          icon={<ReloadOutlined spin={refreshing} />}
          loading={refreshing}
          onClick={() => load(true)}
        >
          Refresh
        </Button>
      </div>

      {queue.failed > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${queue.failed} failed job${queue.failed > 1 ? 's' : ''} in the queue. Review below.`}
          style={{ marginBottom: 16, borderRadius: 10 }}
        />
      )}

      {/* Queue stats */}
      <Card
        title={<Space><ThunderboltOutlined style={{ color: '#0a40d9' }} /><Text strong>BullMQ Email Queue</Text></Space>}
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 20 }}
      >
        <Row gutter={[16, 16]}>
          {queueCards.map(c => (
            <Col xs={12} sm={8} md={4} key={c.label} style={{ flex: '1 1 0' }}>
              <div style={{
                textAlign: 'center', padding: '16px 8px',
                borderRadius: 10, background: `${c.color}12`,
                border: `1px solid ${c.color}30`,
              }}>
                {React.cloneElement(c.icon, { style: { fontSize: 20, color: c.color, display: 'block', margin: '0 auto 8px' } })}
                <div style={{ fontSize: 28, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: tk.colorTextSecondary, marginTop: 4 }}>{c.label}</div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Server info + DB stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12}>
          <Card
            title={<Space><DesktopOutlined style={{ color: '#10b981' }} /><Text strong>Server</Text></Space>}
            style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, height: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {[
                { label: 'Node Version', value: node_version },
                { label: 'Uptime',       value: uptimeFmt() },
                { label: 'Memory (RSS)', value: `${memory_mb} MB` },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>{r.label}</Text>
                  <Text strong style={{ fontSize: 13 }}>{r.value}</Text>
                </div>
              ))}
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card
            title={<Space><DatabaseOutlined style={{ color: '#0a40d9' }} /><Text strong>Database</Text></Space>}
            style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, height: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {[
                { label: 'Total Orgs',      value: db.total_orgs.toLocaleString() },
                { label: 'Total Users',     value: db.total_users.toLocaleString() },
                { label: 'Total Campaigns', value: db.total_campaigns.toLocaleString() },
                { label: 'Total Emails',    value: db.total_emails.toLocaleString() },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>{r.label}</Text>
                  <Text strong style={{ fontSize: 13 }}>{r.value}</Text>
                </div>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Failed jobs */}
      <Card
        title={
          <Space>
            <CloseCircleOutlined style={{ color: queue.failed > 0 ? '#ef4444' : tk.colorTextSecondary }} />
            <Text strong>Failed Jobs</Text>
            {queue.failed > 0 && <Tag color="error">{queue.failed}</Tag>}
          </Space>
        }
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
        styles={{ body: { padding: 0 } }}
      >
        {failed_jobs.length === 0 ? (
          <div style={{ padding: '32px 24px', textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: 32, color: '#10b981', display: 'block', marginBottom: 8 }} />
            <Text type="secondary">No failed jobs — queue is healthy</Text>
          </div>
        ) : (
          <Table
            rowKey="id"
            dataSource={failed_jobs}
            columns={failedCols}
            size="small"
            pagination={false}
            style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
          
          scroll={{ x: 'max-content' }}
        />
        )}
      </Card>
    </div>
  );
};

export default AdminSystem;
